import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { Modal, Form, Button, Card, Alert, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { workoutAPI } from '../services/workoutAPI';
import TemplateForm from './TemplateForm';
import CardioOverridesEditor from './CardioOverridesEditor';
import CopyWeekModal from './CopyWeekModal';

// ==================== HELPER FUNCTIONS ====================

// Analyzes workouts to determine which hierarchical levels are used
const analyzeWorkoutStructure = (workouts) => {
  const structure = {
    usesPhases: false,
    usesWeeks: false,
    usesDays: false,
    hasUnassigned: false
  };

  workouts.forEach(workout => {
    if (workout.phase !== null && workout.phase !== undefined) {
      structure.usesPhases = true;
    }
    if (workout.week !== null && workout.week !== undefined) {
      structure.usesWeeks = true;
    }
    if (workout.day !== null && workout.day !== undefined) {
      structure.usesDays = true;
    }
    if (!workout.phase && !workout.week && !workout.day) {
      structure.hasUnassigned = true;
    }
  });

  return structure;
};

// Groups workouts into hierarchical tree structure
const buildTreeStructure = (workouts, structure) => {
  const tree = {
    unassigned: [],
    hierarchy: []
  };

  // Separate unassigned workouts
  const assigned = [];
  workouts.forEach(workout => {
    if (!workout.phase && !workout.week && !workout.day) {
      tree.unassigned.push(workout);
    } else {
      assigned.push(workout);
    }
  });

  // Build hierarchy based on what's used
  if (structure.usesPhases) {
    tree.hierarchy = groupByPhase(assigned, structure);
  } else if (structure.usesWeeks) {
    tree.hierarchy = groupByWeek(assigned, structure);
  } else if (structure.usesDays) {
    tree.hierarchy = groupByDay(assigned, structure);
  }

  return tree;
};

// Helper: Group workouts by phase
const groupByPhase = (workouts, structure) => {
  const phases = {};

  workouts.forEach(workout => {
    const phase = workout.phase || 'unassigned';
    if (!phases[phase]) {
      phases[phase] = { value: phase, level: 'phase', children: [] };
    }

    if (structure.usesWeeks) {
      // Phase contains weeks
      const week = workout.week || 'unassigned';
      let weekNode = phases[phase].children.find(w => w.value === week);
      if (!weekNode) {
        weekNode = { value: week, level: 'week', children: [] };
        phases[phase].children.push(weekNode);
      }

      if (structure.usesDays) {
        // Week contains days
        const day = workout.day || 'unassigned';
        let dayNode = weekNode.children.find(d => d.value === day);
        if (!dayNode) {
          dayNode = { value: day, level: 'day', workouts: [] };
          weekNode.children.push(dayNode);
        }
        dayNode.workouts.push(workout);
      } else {
        // Week contains workouts directly
        if (!weekNode.workouts) weekNode.workouts = [];
        weekNode.workouts.push(workout);
      }
    } else if (structure.usesDays) {
      // Phase contains days (no weeks)
      const day = workout.day || 'unassigned';
      let dayNode = phases[phase].children.find(d => d.value === day);
      if (!dayNode) {
        dayNode = { value: day, level: 'day', workouts: [] };
        phases[phase].children.push(dayNode);
      }
      dayNode.workouts.push(workout);
    } else {
      // Phase contains workouts directly
      if (!phases[phase].workouts) phases[phase].workouts = [];
      phases[phase].workouts.push(workout);
    }
  });

  // Convert to sorted array
  return Object.entries(phases)
    .sort(([a], [b]) => {
      if (a === 'unassigned') return 1;
      if (b === 'unassigned') return -1;
      return a - b;
    })
    .map(([_, node]) => {
      // Sort children as well
      if (node.children && node.children.length > 0) {
        node.children.sort((a, b) => {
          if (a.value === 'unassigned') return 1;
          if (b.value === 'unassigned') return -1;
          return a.value - b.value;
        });
        // Sort grandchildren
        node.children.forEach(child => {
          if (child.children && child.children.length > 0) {
            child.children.sort((a, b) => {
              if (a.value === 'unassigned') return 1;
              if (b.value === 'unassigned') return -1;
              return a.value - b.value;
            });
          }
        });
      }
      return node;
    });
};

// Helper: Group workouts by week (no phases)
const groupByWeek = (workouts, structure) => {
  const weeks = {};

  workouts.forEach(workout => {
    const week = workout.week || 'unassigned';
    if (!weeks[week]) {
      weeks[week] = { value: week, level: 'week', children: [] };
    }

    if (structure.usesDays) {
      // Week contains days
      const day = workout.day || 'unassigned';
      let dayNode = weeks[week].children.find(d => d.value === day);
      if (!dayNode) {
        dayNode = { value: day, level: 'day', workouts: [] };
        weeks[week].children.push(dayNode);
      }
      dayNode.workouts.push(workout);
    } else {
      // Week contains workouts directly
      if (!weeks[week].workouts) weeks[week].workouts = [];
      weeks[week].workouts.push(workout);
    }
  });

  return Object.entries(weeks)
    .sort(([a], [b]) => {
      if (a === 'unassigned') return 1;
      if (b === 'unassigned') return -1;
      return a - b;
    })
    .map(([_, node]) => {
      // Sort children
      if (node.children && node.children.length > 0) {
        node.children.sort((a, b) => {
          if (a.value === 'unassigned') return 1;
          if (b.value === 'unassigned') return -1;
          return a.value - b.value;
        });
      }
      return node;
    });
};

// Helper: Group workouts by day (no phases/weeks)
const groupByDay = (workouts, structure) => {
  const days = {};

  workouts.forEach(workout => {
    const day = workout.day || 'unassigned';
    if (!days[day]) {
      days[day] = { value: day, level: 'day', workouts: [] };
    }
    days[day].workouts.push(workout);
  });

  return Object.entries(days)
    .sort(([a], [b]) => {
      if (a === 'unassigned') return 1;
      if (b === 'unassigned') return -1;
      return a - b;
    })
    .map(([_, node]) => node);
};

// Generate unique node ID for expand/collapse state
const getNodeId = (level, value, parentId = null) => {
  const cleanValue = value === 'unassigned' ? 'unassigned' : value;
  if (parentId) {
    return `${parentId}-${level}-${cleanValue}`;
  }
  return `${level}-${cleanValue}`;
};

// Get container path for drag & drop validation
const getContainerPath = (workout) => {
  return {
    phase: workout.phase || null,
    week: workout.week || null,
    day: workout.day || null
  };
};

// Check if two container paths are the same
const isSameContainer = (path1, path2) => {
  return path1.phase === path2.phase &&
         path1.week === path2.week &&
         path1.day === path2.day;
};

// ==================== COMPONENTS ====================

// Memoized WorkoutCard component to prevent unnecessary re-renders
const WorkoutCard = memo(({
  workout,
  index,
  totalWorkouts,
  isSaving,
  draggedIndex,
  dragOverIndex,
  onWorkoutChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd
}) => {
  return (
    <Card
      className={`workout-card mb-3 ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
      draggable={!isSaving}
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={onDragEnd}
    >
      <Card.Body>
        <div className="d-flex align-items-start">
          {/* Drag handle */}
          <div className="drag-handle me-3" style={{ cursor: 'grab' }}>
            <i className="fas fa-grip-vertical text-muted"></i>
          </div>

          {/* Workout content */}
          <div className="flex-grow-1">
            {/* Header with badges and delete button */}
            <div className="d-flex justify-content-between align-items-start mb-0">
              <div>
                {/* Structure badges */}
                <div className="mb-0">
                  {workout.phase && (
                    <Badge bg="secondary" className="me-1">
                      Phase {workout.phase}
                    </Badge>
                  )}
                  {workout.week && (
                    <Badge bg="info" className="me-1">
                      Week {workout.week}
                    </Badge>
                  )}
                  {workout.day && (
                    <Badge bg="primary" className="me-1">
                      Day {workout.day}
                    </Badge>
                  )}
                </div>

                {/* Workout name */}
                <h6 className="mb-1">{workout.name}</h6>

                {/* Template badge */}
                <Badge bg="success" className="me-2">
                  <i className="fas fa-file-alt me-1"></i>
                  {workout.template_name}
                </Badge>
              </div>

              {/* Action buttons */}
              <div className="btn-group" role="group">
                {/* Move up button */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onMoveUp(index)}
                  disabled={isSaving || index === 0}
                  title="Move workout up"
                  aria-label="Move workout up"
                >
                  <i className="fas fa-arrow-up"></i>
                </Button>

                {/* Move down button */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onMoveDown(index)}
                  disabled={isSaving || index === totalWorkouts - 1}
                  title="Move workout down"
                  aria-label="Move workout down"
                >
                  <i className="fas fa-arrow-down"></i>
                </Button>

                {/* Delete button */}
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onRemove(workout.id)}
                  disabled={isSaving}
                  title="Remove workout"
                  aria-label="Remove workout"
                >
                  <i className="fas fa-trash"></i>
                </Button>
              </div>
            </div>

            {/* Notes */}
            <Form.Group className="mb-0">
              <Form.Label className="small text-muted">Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={workout.notes}
                onChange={(e) => onWorkoutChange(workout.id, 'notes', e.target.value)}
                placeholder="Optional notes for this workout..."
                disabled={isSaving}
              />
            </Form.Group>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
});

WorkoutCard.displayName = 'WorkoutCard';

// TreeNode component for hierarchical navigation
const TreeNode = memo(({
  node,
  parentId,
  depth,
  expandedNodes,
  onToggle,
  onWorkoutChange,
  onRemove,
  onEdit,
  onEditTemplate,
  onEditOverrides,
  onCopyWeek,
  inlineTemplates,
  isSaving,
  draggedWorkout,
  dragOverWorkout,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd
}) => {
  const nodeId = getNodeId(node.level, node.value, parentId);
  const isExpanded = expandedNodes.has(nodeId);
  const hasChildren = node.children && node.children.length > 0;
  const hasWorkouts = node.workouts && node.workouts.length > 0;

  // Calculate total workout count in this node and children
  const countWorkouts = (n) => {
    let count = n.workouts ? n.workouts.length : 0;
    if (n.children) {
      n.children.forEach(child => {
        count += countWorkouts(child);
      });
    }
    return count;
  };
  const workoutCount = countWorkouts(node);

  // Get label for node
  const getLabel = () => {
    if (node.value === 'unassigned') {
      return `Unassigned ${node.level}s`;
    }
    const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);
    const baseLabel = `${capitalize(node.level)} ${node.value}`;

    // For day nodes, append workout name(s) if available
    if (node.level === 'day' && node.workouts && node.workouts.length > 0) {
      const workoutNames = node.workouts.map(w => w.name).join(', ');
      return `${baseLabel} - ${workoutNames}`;
    }

    return baseLabel;
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowRight':
        if (!isExpanded && (hasChildren || hasWorkouts)) {
          onToggle(nodeId);
        }
        e.preventDefault();
        break;
      case 'ArrowLeft':
        if (isExpanded) {
          onToggle(nodeId);
        }
        e.preventDefault();
        break;
      case 'Enter':
      case ' ':
        if (hasChildren || hasWorkouts) {
          onToggle(nodeId);
        }
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  return (
    <div className="tree-node" style={{ marginLeft: `${depth * 0}px` }}>
      {/* Node header */}
      <div
        className="tree-node-header"
        onClick={() => onToggle(nodeId)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={isExpanded}
        aria-label={`${getLabel()}, ${workoutCount} workout${workoutCount !== 1 ? 's' : ''}`}
      >
        {/* Chevron icon */}
        <i
          className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} chevron-icon`}
          aria-hidden="true"
        ></i>

        {/* Node label */}
        <span className="node-label">{getLabel()}</span>

        {/* Workout count badge */}
        <Badge bg="secondary" className="node-badge">
          {workoutCount}
        </Badge>

        {/* Copy Week button - only show for week nodes */}
        {node.level === 'week' && node.value !== 'unassigned' && onCopyWeek && (
          <Button
            variant="outline-primary"
            size="sm"
            className="ms-2 copy-week-btn"
            onClick={(e) => {
              e.stopPropagation(); // Don't toggle expand
              onCopyWeek(node);
            }}
            disabled={isSaving}
            title="Copy this week's workouts to another week"
            aria-label="Copy week"
          >
            <i className="fas fa-copy"></i>
          </Button>
        )}
      </div>

      {/* Node children */}
      {isExpanded && (
        <div className="tree-node-children">
          {/* Render child nodes */}
          {hasChildren && node.children.map((child, index) => (
            <TreeNode
              key={`${nodeId}-${child.level}-${child.value}`}
              node={child}
              parentId={nodeId}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onWorkoutChange={onWorkoutChange}
              onRemove={onRemove}
              onEdit={onEdit}
              onEditTemplate={onEditTemplate}
              onEditOverrides={onEditOverrides}
              onCopyWeek={onCopyWeek}
              inlineTemplates={inlineTemplates}
              isSaving={isSaving}
              draggedWorkout={draggedWorkout}
              dragOverWorkout={dragOverWorkout}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
            />
          ))}

          {/* Render workouts at this level */}
          {hasWorkouts && node.workouts.map((workout, index) => {
            const containerPath = getContainerPath(workout);
            const isDragging = draggedWorkout && draggedWorkout.workout.id === workout.id;
            const isDragOver = dragOverWorkout && dragOverWorkout.id === workout.id;

            return (
              <Card
                key={workout.id}
                id={`workout-${workout.id}`}
                className={`workout-card mb-3 depth-${depth + 1} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                draggable={!isSaving}
                onDragStart={(e) => onDragStart(e, workout, containerPath)}
                onDragOver={(e) => onDragOver(e, workout, containerPath)}
                onDragLeave={onDragLeave}
                onDrop={(e) => onDrop(e, workout, containerPath)}
                onDragEnd={onDragEnd}
              >
                <Card.Body>
                  <div className="d-flex align-items-start">
                    {/* Drag handle */}
                    <div className="drag-handle me-3" style={{ cursor: 'grab' }}>
                      <i className="fas fa-grip-vertical text-muted"></i>
                    </div>

                    {/* Workout content */}
                    <div className="flex-grow-1">
                      {/* Header with badges and delete button */}
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          {/* Structure badges */}
                          <div className="mb-2">
                            {workout.phase && (
                              <Badge bg="secondary" className="me-1">
                                Phase {workout.phase}
                              </Badge>
                            )}
                            {workout.week && (
                              <Badge bg="info" className="me-1">
                                Week {workout.week}
                              </Badge>
                            )}
                            {workout.day && (
                              <Badge bg="primary" className="me-1">
                                Day {workout.day}
                              </Badge>
                            )}
                          </div>

                          {/* Workout name */}
                          <h6 className="mb-1">{workout.name}</h6>

                          {/* Template badge */}
                          <Badge bg="success" className="me-2">
                            <i className="fas fa-file-alt me-1"></i>
                            {workout.template_name}
                          </Badge>

                          {/* Cardio overrides badge */}
                          {workout.cardio_overrides && (
                            <Badge bg="warning" className="me-2">
                              <i className="fas fa-running me-1"></i>
                              Overrides Active
                            </Badge>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className="btn-group" role="group">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => onEdit(workout)}
                            disabled={isSaving}
                            title="Edit workout"
                            aria-label="Edit workout"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          {inlineTemplates.some(t => t.id === workout.template_id) && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              onClick={() => onEditTemplate(workout)}
                              disabled={isSaving}
                              title="Edit inline template"
                              aria-label="Edit inline template"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                          )}
                          <Button
                            variant={workout.cardio_overrides ? "warning" : "outline-secondary"}
                            size="sm"
                            onClick={() => onEditOverrides(workout)}
                            disabled={isSaving}
                            title="Override cardio parameters"
                            aria-label="Override cardio parameters"
                          >
                            <i className="fas fa-running"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => onRemove(workout.id)}
                            disabled={isSaving}
                            title="Remove workout"
                            aria-label="Remove workout"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </div>

                      {/* Notes */}
                      <Form.Group className="mb-0">
                        <Form.Label className="small text-muted">Notes</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={workout.notes}
                          onChange={(e) => onWorkoutChange(workout.id, 'notes', e.target.value)}
                          placeholder="Optional notes for this workout..."
                          disabled={isSaving}
                        />
                      </Form.Group>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
});

TreeNode.displayName = 'TreeNode';

// WorkoutTreeView component - main tree container
const WorkoutTreeView = memo(({
  workouts,
  expandedNodes,
  onToggle,
  onExpandAll,
  onCollapseAll,
  onWorkoutChange,
  onRemove,
  onEdit,
  onEditTemplate,
  onEditOverrides,
  onCopyWeek,
  inlineTemplates,
  isSaving,
  draggedWorkout,
  dragOverWorkout,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd
}) => {
  // Generate tree structure
  const treeData = useMemo(() => {
    const structure = analyzeWorkoutStructure(workouts);
    return {
      structure,
      tree: buildTreeStructure(workouts, structure)
    };
  }, [workouts]);

  const { tree } = treeData;

  // If no workouts, show empty state
  if (workouts.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="fas fa-clipboard-list fa-3x mb-3"></i>
        <p>No workouts added yet. Use the form above to add workouts to your program.</p>
      </div>
    );
  }

  return (
    <div className="workout-tree-view">
      {/* Tree actions */}
      <div className="tree-actions mb-3">
        <Button variant="outline-secondary" size="sm" onClick={onExpandAll}>
          <i className="fas fa-plus-square me-1"></i>
          Expand All
        </Button>
        <Button variant="outline-secondary" size="sm" onClick={onCollapseAll} className="ms-2">
          <i className="fas fa-minus-square me-1"></i>
          Collapse All
        </Button>
      </div>

      {/* Unassigned section */}
      {tree.unassigned.length > 0 && (
        <div className="unassigned-section mb-3">
          <div className="unassigned-header">
            <i className="fas fa-question-circle me-2"></i>
            <strong>Unassigned Workouts</strong>
            <Badge bg="warning" className="ms-2">{tree.unassigned.length}</Badge>
          </div>
          <div className="mt-3">
            {tree.unassigned.map((workout) => {
              const containerPath = getContainerPath(workout);
              const isDragging = draggedWorkout && draggedWorkout.workout.id === workout.id;
              const isDragOver = dragOverWorkout && dragOverWorkout.id === workout.id;

              return (
                <Card
                  key={workout.id}
                  id={`workout-${workout.id}`}
                  className={`workout-card mb-3 ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                  draggable={!isSaving}
                  onDragStart={(e) => onDragStart(e, workout, containerPath)}
                  onDragOver={(e) => onDragOver(e, workout, containerPath)}
                  onDragLeave={onDragLeave}
                  onDrop={(e) => onDrop(e, workout, containerPath)}
                  onDragEnd={onDragEnd}
                >
                  <Card.Body>
                    <div className="d-flex align-items-start">
                      {/* Drag handle */}
                      <div className="drag-handle me-3" style={{ cursor: 'grab' }}>
                        <i className="fas fa-grip-vertical text-muted"></i>
                      </div>

                      {/* Workout content */}
                      <div className="flex-grow-1">
                        {/* Header with action buttons */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            {/* Workout name */}
                            <h6 className="mb-1">{workout.name}</h6>

                            {/* Template badge */}
                            <Badge bg="success" className="me-2">
                              <i className="fas fa-file-alt me-1"></i>
                              {workout.template_name}
                            </Badge>
                          </div>

                          {/* Action buttons */}
                          <div className="btn-group" role="group">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => onEdit(workout)}
                              disabled={isSaving}
                              title="Edit workout"
                              aria-label="Edit workout"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            {inlineTemplates.some(t => t.id === workout.template_id) && (
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => onEditTemplate(workout)}
                                disabled={isSaving}
                                title="Edit inline template"
                                aria-label="Edit inline template"
                              >
                                <i className="fas fa-edit"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => onRemove(workout.id)}
                              disabled={isSaving}
                              title="Remove workout"
                              aria-label="Remove workout"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </div>

                        {/* Notes */}
                        <Form.Group className="mb-0">
                          <Form.Label className="small text-muted">Notes</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={workout.notes}
                            onChange={(e) => onWorkoutChange(workout.id, 'notes', e.target.value)}
                            placeholder="Optional notes for this workout..."
                            disabled={isSaving}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Hierarchical tree */}
      {tree.hierarchy.length > 0 && (
        <div className="hierarchy-tree">
          {tree.hierarchy.map((node) => (
            <TreeNode
              key={`${node.level}-${node.value}`}
              node={node}
              parentId={null}
              depth={0}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onWorkoutChange={onWorkoutChange}
              onRemove={onRemove}
              onEdit={onEdit}
              onEditTemplate={onEditTemplate}
              onEditOverrides={onEditOverrides}
              onCopyWeek={onCopyWeek}
              inlineTemplates={inlineTemplates}
              isSaving={isSaving}
              draggedWorkout={draggedWorkout}
              dragOverWorkout={dragOverWorkout}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onDragEnd={onDragEnd}
            />
          ))}
        </div>
      )}
    </div>
  );
});

WorkoutTreeView.displayName = 'WorkoutTreeView';

function ProgramForm({ show, onHide, program, onSave, error}) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    notes: '',
    workouts: [] // Array of workout items
  });

  const [errors, setErrors] = useState({});
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [inlineTemplates, setInlineTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Track initial form state to detect unsaved changes
  const [initialFormData, setInitialFormData] = useState(null);

  // Template creation modal
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // Track which template is being edited

  // Track if program was auto-saved (for new programs that need inline templates)
  const [autoSavedProgramId, setAutoSavedProgramId] = useState(null);

  // Track loading state for copy operation
  const [copyingTemplate, setCopyingTemplate] = useState(false);

  // Workout addition/edit form
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [phase, setPhase] = useState('');
  const [week, setWeek] = useState('');
  const [day, setDay] = useState('');
  const [workoutName, setWorkoutName] = useState('');
  const [editingWorkoutId, setEditingWorkoutId] = useState(null); // Track which workout is being edited

  // Cardio overrides editor
  const [showOverridesEditor, setShowOverridesEditor] = useState(false);
  const [editingOverridesWorkoutId, setEditingOverridesWorkoutId] = useState(null);
  const [currentOverridesTemplate, setCurrentOverridesTemplate] = useState(null);

  // Drag and drop (modified for tree view)
  const [draggedWorkout, setDraggedWorkout] = useState(null); // { workout, containerPath }
  const [dragOverWorkout, setDragOverWorkout] = useState(null); // workout object

  // Tree view expand/collapse state
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Copy week feature
  const [showCopyWeekModal, setShowCopyWeekModal] = useState(false);
  const [copyWeekSource, setCopyWeekSource] = useState(null); // { week, phase }

  useEffect(() => {
    let isMounted = true;

    const loadTemplates = async () => {
      setIsLoadingTemplates(true);
      try {
        // Pass program.id to fetch both global + inline templates for this program
        const programId = program?.id || autoSavedProgramId || null;
        const data = await workoutAPI.fetchTemplates(programId);

        if (isMounted) {
          // Separate global and inline templates
          const global = data.filter(t => !t.is_inline);
          const inline = data.filter(t => t.is_inline);

          setAvailableTemplates(global);
          setInlineTemplates(inline);
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching templates:', err);
        }
      } finally {
        if (isMounted) {
          setIsLoadingTemplates(false);
        }
      }
    };

    if (show) {
      loadTemplates();
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [show, program?.id, autoSavedProgramId]);

  // Load and persist expanded nodes state from localStorage
  useEffect(() => {
    const currentProgramId = program?.id || autoSavedProgramId;
    if (!show || !currentProgramId) return;

    // Load saved state
    const savedState = localStorage.getItem(`programForm-expanded-${currentProgramId}`);
    if (savedState) {
      try {
        setExpandedNodes(new Set(JSON.parse(savedState)));
      } catch (err) {
        console.error('Error loading expanded state:', err);
      }
    } else {
      // Default: expand first level
      if (formData.workouts.length > 0) {
        const structure = analyzeWorkoutStructure(formData.workouts);
        const firstLevel = new Set();

        if (structure.usesPhases) {
          formData.workouts.forEach(w => {
            if (w.phase) firstLevel.add(getNodeId('phase', w.phase));
          });
        } else if (structure.usesWeeks) {
          formData.workouts.forEach(w => {
            if (w.week) firstLevel.add(getNodeId('week', w.week));
          });
        } else if (structure.usesDays) {
          formData.workouts.forEach(w => {
            if (w.day) firstLevel.add(getNodeId('day', w.day));
          });
        }

        setExpandedNodes(firstLevel);
      }
    }
  }, [show, program?.id, autoSavedProgramId, formData.workouts]);

  // Save expanded state to localStorage
  useEffect(() => {
    const currentProgramId = program?.id || autoSavedProgramId;
    if (currentProgramId && expandedNodes.size > 0) {
      localStorage.setItem(
        `programForm-expanded-${currentProgramId}`,
        JSON.stringify([...expandedNodes])
      );
    }
  }, [expandedNodes, program?.id, autoSavedProgramId]);

  useEffect(() => {
    if (program) {
      const initialData = {
        name: program.name || '',
        description: program.description || '',
        notes: program.notes || '',
        workouts: (program.workouts || []).map((workout, index) => ({
          id: Date.now() + index,
          template_id: workout.template_id,
          template_name: workout.template_name,
          phase: workout.phase || null,
          week: workout.week || null,
          day: workout.day || null,
          name: workout.name || '',
          sort_order: workout.sort_order || index,
          notes: workout.notes || '',
          cardio_overrides: workout.cardio_overrides || null
        }))
      };
      setFormData(initialData);
      setInitialFormData(JSON.parse(JSON.stringify(initialData))); // Deep copy
    } else {
      const initialData = {
        name: '',
        description: '',
        notes: '',
        workouts: []
      };
      setFormData(initialData);
      setInitialFormData(JSON.parse(JSON.stringify(initialData))); // Deep copy
    }
    setErrors({});
    setAutoSavedProgramId(null); // Reset auto-saved program ID when opening/closing modal
  }, [program, show]);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!initialFormData) return false;

    // Compare form data with initial data
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);

  // Handle modal close with unsaved changes check
  const handleClose = useCallback(() => {
    if (hasUnsavedChanges()) {
      const confirmClose = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmClose) {
        return; // Don't close
      }
    }
    onHide();
  }, [hasUnsavedChanges, onHide]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleEditWorkout = useCallback((workout) => {
    // Find the template option
    const templateOption = {
      value: workout.template_id,
      label: workout.template_name,
      isInline: inlineTemplates.some(t => t.id === workout.template_id)
    };

    setSelectedTemplate(templateOption);
    setPhase(workout.phase || '');
    setWeek(workout.week || '');
    setDay(workout.day || '');
    setWorkoutName(workout.name);
    setEditingWorkoutId(workout.id);

    // Clear any errors
    setErrors(prev => ({ ...prev, template: null, workoutName: null, phase: null, week: null, day: null }));

    // Scroll to form
    setTimeout(() => {
      const formElement = document.querySelector('.add-workout-toolbar');
      formElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [inlineTemplates]);

  const handleEditTemplateFromWorkout = useCallback(async (workout) => {
    // Check if this is an inline template
    const isInline = inlineTemplates.some(t => t.id === workout.template_id);
    if (!isInline) {
      alert('Only inline templates can be edited from here. This is a global template.');
      return;
    }

    try {
      // Fetch full template details
      const fullTemplate = await workoutAPI.fetchTemplateById(workout.template_id);
      setEditingTemplate(fullTemplate);
      setShowTemplateForm(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      alert('Failed to load template details. Please try again.');
    }
  }, [inlineTemplates]);

  const handleEditOverrides = useCallback(async (workout) => {
    try {
      // Fetch full template details
      const fullTemplate = await workoutAPI.fetchTemplateById(workout.template_id);

      // Check if template has any cardio exercises
      const hasCardio = fullTemplate.exercises?.some(ex =>
        ex.exercise_type === 'cardio_basic' || ex.exercise_type === 'cardio_segments'
      );

      if (!hasCardio) {
        alert('This template has no cardio exercises to override.');
        return;
      }

      setEditingOverridesWorkoutId(workout.id);
      setCurrentOverridesTemplate(fullTemplate);
      setShowOverridesEditor(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      alert('Failed to load template details. Please try again.');
    }
  }, []);

  const handleSaveOverrides = useCallback((overrides) => {
    setFormData(prev => ({
      ...prev,
      workouts: prev.workouts.map(w =>
        w.id === editingOverridesWorkoutId
          ? { ...w, cardio_overrides: overrides }
          : w
      )
    }));
    setShowOverridesEditor(false);
    setEditingOverridesWorkoutId(null);
    setCurrentOverridesTemplate(null);
  }, [editingOverridesWorkoutId]);

  const handleCreateInlineTemplate = async () => {
    // Check if we need to auto-save the program first
    const currentProgramId = program?.id || autoSavedProgramId;

    if (!currentProgramId) {
      // Need to auto-save the program first
      if (!formData.name.trim()) {
        setErrors(prev => ({ ...prev, name: 'Please enter a program name before creating inline templates' }));
        // Scroll to the program name field
        setTimeout(() => {
          const nameField = document.querySelector('input[name="name"]');
          nameField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          nameField?.focus();
        }, 100);
        return;
      }

      // Auto-save the program
      setIsSaving(true);
      try {
        const programData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          notes: formData.notes.trim(),
          workouts: [] // Empty workouts for now
        };

        const result = await workoutAPI.createProgram(programData);
        setAutoSavedProgramId(result.id);

        // Now open the template form
        setShowTemplateForm(true);
      } catch (err) {
        console.error('Error auto-saving program:', err);
        alert('Failed to save program. Please try again.');
      } finally {
        setIsSaving(false);
      }
    } else {
      // Program already exists, just open the template form
      setShowTemplateForm(true);
    }
  };

  // Generate smart versioned name for copied template
  const generateSmartTemplateName = useCallback((sourceTemplate, existingTemplates) => {
    const baseName = sourceTemplate.label || sourceTemplate.name;

    // Escape special regex characters in base name
    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Check if name already has version suffix (v2, v3, etc.)
    const versionMatch = baseName.match(/^(.+?)\s+v(\d+)$/);

    if (versionMatch) {
      // Name already has version: "Upper Body v2"
      const nameWithoutVersion = versionMatch[1];
      const currentVersion = parseInt(versionMatch[2]);

      // Find highest version number for this base name
      let highestVersion = currentVersion;
      existingTemplates.forEach(template => {
        const match = template.label.match(new RegExp(`^${escapeRegex(nameWithoutVersion)}\\s+v(\\d+)$`));
        if (match) {
          highestVersion = Math.max(highestVersion, parseInt(match[1]));
        }
      });

      return `${nameWithoutVersion} v${highestVersion + 1}`;
    } else {
      // Name doesn't have version
      // Check if base name or any variants exist
      const baseExists = existingTemplates.some(t =>
        t.label === baseName || t.label.startsWith(`${baseName} v`)
      );

      if (!baseExists) {
        return `${baseName} v2`; // First variant
      } else {
        // Find highest version
        let highestVersion = 1;
        existingTemplates.forEach(template => {
          if (template.label === baseName) {
            highestVersion = Math.max(highestVersion, 1);
          }
          const match = template.label.match(new RegExp(`^${escapeRegex(baseName)}\\s+v(\\d+)$`));
          if (match) {
            highestVersion = Math.max(highestVersion, parseInt(match[1]));
          }
        });

        return `${baseName} v${highestVersion + 1}`;
      }
    }
  }, []);

  // Copy template and customize
  const handleCopyAndCustomize = async () => {
    if (!selectedTemplate) {
      alert('Please select a template to copy');
      return;
    }

    // Check if we need to auto-save the program first (for inline template creation)
    const currentProgramId = program?.id || autoSavedProgramId;

    if (!currentProgramId) {
      // Need to auto-save the program first
      if (!formData.name.trim()) {
        setErrors(prev => ({
          ...prev,
          name: 'Please enter a program name before creating inline templates'
        }));
        setTimeout(() => {
          const nameField = document.querySelector('input[name="name"]');
          nameField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          nameField?.focus();
        }, 100);
        return;
      }

      // Auto-save the program
      setIsSaving(true);
      try {
        const programData = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          notes: formData.notes.trim(),
          workouts: []
        };

        const result = await workoutAPI.createProgram(programData);
        setAutoSavedProgramId(result.id);

        // Continue with copy operation
        await performCopyAndCustomize(result.id);
      } catch (err) {
        console.error('Error auto-saving program:', err);
        alert('Failed to save program. Please try again.');
      } finally {
        setIsSaving(false);
      }
    } else {
      // Program already exists, perform copy
      await performCopyAndCustomize(currentProgramId);
    }
  };

  const performCopyAndCustomize = async (currentProgramId) => {
    setCopyingTemplate(true);

    try {
      // 1. Fetch full template details
      const fullTemplate = await workoutAPI.fetchTemplateById(selectedTemplate.value);

      // 2. Generate smart name
      const allTemplateOptions = [
        ...inlineTemplates.map(t => ({ label: t.name, value: t.id })),
        ...availableTemplates.map(t => ({ label: t.name, value: t.id }))
      ];
      const newName = generateSmartTemplateName(selectedTemplate, allTemplateOptions);

      // 3. Prepare copied template data (remove ID to create new)
      const copiedTemplate = {
        ...fullTemplate,
        id: undefined, // Remove ID so it creates new template
        name: newName,
        // All other fields preserved (exercises, description, notes, etc.)
      };

      // 4. Set as editing template and open form
      setEditingTemplate(copiedTemplate);
      setShowTemplateForm(true);

    } catch (err) {
      console.error('Error copying template:', err);
      alert('Failed to load template details. Please try again.');
    } finally {
      setCopyingTemplate(false);
    }
  };

  const handleCancelEdit = () => {
    setSelectedTemplate(null);
    // Keep phase, week, and day values sticky
    setWorkoutName('');
    setEditingWorkoutId(null);
    setErrors(prev => ({ ...prev, template: null, workoutName: null, phase: null, week: null, day: null }));
  };

  const handleAddWorkout = () => {
    // Clear previous errors and validate all fields
    const newErrors = {};

    // Validate template
    if (!selectedTemplate) {
      newErrors.template = 'Please select a template';
    }

    // Auto-populate workout name from template if empty
    let finalWorkoutName = workoutName.trim();
    if (!finalWorkoutName && selectedTemplate) {
      finalWorkoutName = selectedTemplate.label;
    }

    // Validate workout name
    if (!finalWorkoutName) {
      newErrors.workoutName = 'Please enter a workout name or select a template';
    } else if (finalWorkoutName.length > 255) {
      newErrors.workoutName = 'Workout name too long (max 255 characters)';
    }

    // Validate phase (if provided)
    if (phase && phase.toString().trim()) {
      const phaseNum = parseInt(phase);
      if (isNaN(phaseNum) || phaseNum < 1) {
        newErrors.phase = 'Phase must be a positive number (1 or greater)';
      }
    }

    // Validate week (if provided)
    if (week && week.toString().trim()) {
      const weekNum = parseInt(week);
      if (isNaN(weekNum) || weekNum < 1) {
        newErrors.week = 'Week must be a positive number (1 or greater)';
      }
    }

    // Validate day (if provided)
    if (day && day.toString().trim()) {
      const dayNum = parseInt(day);
      if (isNaN(dayNum) || dayNum < 1) {
        newErrors.day = 'Day must be a positive number (1 or greater)';
      }
    }

    // If there are any errors, set them and return
    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...newErrors }));
      return;
    }

    // Check if we're editing or adding
    if (editingWorkoutId) {
      // Update existing workout
      setFormData(prev => ({
        ...prev,
        workouts: prev.workouts.map(w =>
          w.id === editingWorkoutId
            ? {
                ...w,
                template_id: selectedTemplate.value,
                template_name: selectedTemplate.label,
                phase: phase ? parseInt(phase) : null,
                week: week ? parseInt(week) : null,
                day: day ? parseInt(day) : null,
                name: finalWorkoutName
              }
            : w
        )
      }));
    } else {
      // Add new workout
      const newWorkout = {
        id: Date.now(),
        template_id: selectedTemplate.value,
        template_name: selectedTemplate.label,
        phase: phase ? parseInt(phase) : null,
        week: week ? parseInt(week) : null,
        day: day ? parseInt(day) : null,
        name: finalWorkoutName,
        sort_order: formData.workouts.length,
        notes: '',
        cardio_overrides: null
      };

      setFormData(prev => ({
        ...prev,
        workouts: [...prev.workouts, newWorkout]
      }));
    }

    const targetWorkout = editingWorkoutId
      ? formData.workouts.find(w => w.id === editingWorkoutId)
      : {
          phase: phase ? parseInt(phase) : null,
          week: week ? parseInt(week) : null,
          day: day ? parseInt(day) : null,
          id: editingWorkoutId || Date.now()
        };

    // Auto-expand tree path to show workout
    if (targetWorkout && (targetWorkout.phase || targetWorkout.week || targetWorkout.day)) {
      const nodesToExpand = [];

      if (targetWorkout.phase) {
        nodesToExpand.push(getNodeId('phase', targetWorkout.phase));
      }
      if (targetWorkout.week) {
        const parentId = targetWorkout.phase ? getNodeId('phase', targetWorkout.phase) : null;
        nodesToExpand.push(getNodeId('week', targetWorkout.week, parentId));
      }
      if (targetWorkout.day) {
        let parentId = null;
        if (targetWorkout.phase && targetWorkout.week) {
          parentId = getNodeId('week', targetWorkout.week, getNodeId('phase', targetWorkout.phase));
        } else if (targetWorkout.week) {
          parentId = getNodeId('week', targetWorkout.week);
        } else if (targetWorkout.phase) {
          parentId = getNodeId('phase', targetWorkout.phase);
        }
        nodesToExpand.push(getNodeId('day', targetWorkout.day, parentId));
      }

      setExpandedNodes(prev => new Set([...prev, ...nodesToExpand]));

      // Scroll to workout (next tick)
      setTimeout(() => {
        const element = document.getElementById(`workout-${targetWorkout.id}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }

    // Clear the form (keep phase/week/day sticky) and errors
    setSelectedTemplate(null);
    // Keep phase, week, and day values for easy consecutive additions
    setWorkoutName('');
    setEditingWorkoutId(null);
    setErrors(prev => ({ ...prev, template: null, workoutName: null, phase: null, week: null, day: null }));
  };

  const handleEditInlineTemplate = async (template) => {
    try {
      // Fetch full template details
      const fullTemplate = await workoutAPI.fetchTemplateById(template.value);
      setEditingTemplate(fullTemplate);
      setShowTemplateForm(true);
    } catch (err) {
      console.error('Error fetching template details:', err);
      alert('Failed to load template details. Please try again.');
    }
  };

  const handleSaveTemplate = async (templateData) => {
    try {
      const currentProgramId = program?.id || autoSavedProgramId;

      if (editingTemplate) {
        // Update existing template
        await workoutAPI.updateTemplate(editingTemplate.id, templateData);

        // Update all workouts that use this template with the new name
        setFormData(prev => ({
          ...prev,
          workouts: prev.workouts.map(w =>
            w.template_id === editingTemplate.id
              ? { ...w, template_name: templateData.name }
              : w
          )
        }));
      } else {
        // Create new template
        await workoutAPI.createTemplate(templateData, currentProgramId);
      }

      // Reload templates
      const updatedTemplates = await workoutAPI.fetchTemplates(currentProgramId);
      const inline = updatedTemplates.filter(t => t.is_inline);
      const global = updatedTemplates.filter(t => !t.is_inline);

      setInlineTemplates(inline);
      setAvailableTemplates(global);
      setShowTemplateForm(false);
      setEditingTemplate(null);

      // If editing, update the selected template if it's currently selected
      if (editingTemplate && selectedTemplate?.value === editingTemplate.id) {
        setSelectedTemplate({
          value: editingTemplate.id,
          label: templateData.name,
          isInline: true
        });
      }

      // If creating, auto-select the newly created template
      if (!editingTemplate) {
        const newTemplate = updatedTemplates.find(t => t.name === templateData.name && t.is_inline);
        if (newTemplate) {
          setSelectedTemplate({
            value: newTemplate.id,
            label: templateData.name,
            isInline: true
          });
        }
      }
    } catch (err) {
      console.error('Error saving inline template:', err);
      alert('Failed to save inline template. Please try again.');
    }
  };

  const handleWorkoutChange = useCallback((workoutId, field, value) => {
    setFormData(prev => ({
      ...prev,
      workouts: prev.workouts.map(w =>
        w.id === workoutId ? { ...w, [field]: value } : w
      )
    }));
  }, []);

  const handleRemoveWorkout = useCallback((workoutId) => {
    // Find the workout to get its name for the confirmation dialog
    const workout = formData.workouts.find(w => w.id === workoutId);
    if (!workout) return;

    // Show confirmation dialog
    const confirmDelete = window.confirm(
      `Are you sure you want to remove "${workout.name}"?\n\nThis will remove the workout from this program.`
    );

    if (!confirmDelete) {
      return; // User cancelled, don't remove
    }

    setFormData(prev => {
      const filtered = prev.workouts.filter(w => w.id !== workoutId);
      // Only update sort_order if it actually changed
      const updated = filtered.map((w, index) =>
        w.sort_order === index ? w : { ...w, sort_order: index }
      );
      return { ...prev, workouts: updated };
    });
  }, [formData.workouts]);

  // Tree view expand/collapse handlers
  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const allNodeIds = new Set();
    const structure = analyzeWorkoutStructure(formData.workouts);
    const tree = buildTreeStructure(formData.workouts, structure);

    const addNodeIds = (nodes, parentId = null) => {
      nodes.forEach(node => {
        const nodeId = getNodeId(node.level, node.value, parentId);
        allNodeIds.add(nodeId);
        if (node.children && node.children.length > 0) {
          addNodeIds(node.children, nodeId);
        }
      });
    };

    addNodeIds(tree.hierarchy);
    setExpandedNodes(allNodeIds);
  }, [formData.workouts]);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  // Drag and drop handlers (modified for tree view)
  const handleDragStart = (e, workout, containerPath) => {
    setDraggedWorkout({ workout, containerPath });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, dropWorkout, dropContainerPath) => {
    e.preventDefault();

    if (!draggedWorkout) return;

    // Check if same container
    if (!isSameContainer(draggedWorkout.containerPath, dropContainerPath)) {
      e.dataTransfer.dropEffect = 'none';
      setDragOverWorkout(null);
      return;
    }

    if (draggedWorkout.workout.id === dropWorkout.id) return;

    e.dataTransfer.dropEffect = 'move';
    setDragOverWorkout(dropWorkout);
  };

  const handleDragLeave = () => {
    setDragOverWorkout(null);
  };

  const handleDrop = (e, dropWorkout, dropContainerPath) => {
    e.preventDefault();

    if (!draggedWorkout) return;

    // Validate same container
    if (!isSameContainer(draggedWorkout.containerPath, dropContainerPath)) {
      alert('Cannot move workout across different days/weeks/phases. Workouts can only be reordered within the same container.');
      setDraggedWorkout(null);
      setDragOverWorkout(null);
      return;
    }

    if (draggedWorkout.workout.id === dropWorkout.id) {
      setDraggedWorkout(null);
      setDragOverWorkout(null);
      return;
    }

    // Find indices
    const draggedIdx = formData.workouts.findIndex(w => w.id === draggedWorkout.workout.id);
    const dropIdx = formData.workouts.findIndex(w => w.id === dropWorkout.id);

    if (draggedIdx === -1 || dropIdx === -1) return;

    // Reorder
    const workoutsCopy = [...formData.workouts];
    const [moved] = workoutsCopy.splice(draggedIdx, 1);
    workoutsCopy.splice(dropIdx, 0, moved);

    // Update sort orders
    const updatedWorkouts = workoutsCopy.map((w, index) => ({
      ...w,
      sort_order: index
    }));

    setFormData(prev => ({
      ...prev,
      workouts: updatedWorkouts
    }));

    setDraggedWorkout(null);
    setDragOverWorkout(null);
  };

  const handleDragEnd = () => {
    setDraggedWorkout(null);
    setDragOverWorkout(null);
  };

  // Note: handleMoveUp and handleMoveDown removed - tree view handles ordering via drag & drop

  // Copy week handlers
  const replaceWeekInName = useCallback((name, sourceWeek, targetWeek) => {
    // Try to replace "Week X" patterns
    const weekPattern = new RegExp(`Week\\s+${sourceWeek}\\b`, 'gi');
    if (weekPattern.test(name)) {
      return name.replace(weekPattern, `Week ${targetWeek}`);
    }

    // If no week pattern found, append week info
    return `${name} (Week ${targetWeek})`;
  }, []);

  const handleCopyWeek = useCallback((weekNode) => {
    // Extract week and phase from node
    const sourceWeek = weekNode.value;

    // Find phase if exists (look at workouts in this week)
    const weekWorkouts = formData.workouts.filter(w => w.week === sourceWeek);
    const sourcePhase = weekWorkouts[0]?.phase || null;

    setCopyWeekSource({ week: sourceWeek, phase: sourcePhase });
    setShowCopyWeekModal(true);
  }, [formData.workouts]);

  const handleCopyWeekConfirm = useCallback(async (targetWeek) => {
    if (!copyWeekSource) return;

    const { week: sourceWeek, phase: sourcePhase } = copyWeekSource;

    // Filter source workouts
    const sourceWorkouts = formData.workouts.filter(
      w => w.week === sourceWeek && (sourcePhase === null || w.phase === sourcePhase)
    );

    if (sourceWorkouts.length === 0) {
      alert('No workouts found in source week');
      return;
    }

    // Close modal and show loading
    setShowCopyWeekModal(false);
    setIsSaving(true);

    try {
      const currentProgramId = program?.id || autoSavedProgramId;

      // Find existing target workouts for sort order calculation
      const allWorkouts = formData.workouts;
      const maxSortOrder = allWorkouts.length > 0
        ? Math.max(...allWorkouts.map(w => w.sort_order || 0))
        : -1;

      // Copy templates and create new workouts
      const newWorkouts = [];

      for (let index = 0; index < sourceWorkouts.length; index++) {
        const w = sourceWorkouts[index];
        let newTemplateId = w.template_id;
        let newTemplateName = w.template_name;

        // Check if this workout uses an inline template
        const isInlineTemplate = inlineTemplates.some(t => t.id === w.template_id);

        if (isInlineTemplate) {
          // Copy the inline template
          try {
            // 1. Fetch full template details
            const fullTemplate = await workoutAPI.fetchTemplateById(w.template_id);

            // 2. Generate smart name for copied template
            const allTemplateOptions = [
              ...inlineTemplates.map(t => ({ label: t.name, value: t.id })),
              ...availableTemplates.map(t => ({ label: t.name, value: t.id }))
            ];
            const newName = generateSmartTemplateName(
              { label: fullTemplate.name, value: fullTemplate.id },
              allTemplateOptions
            );

            // 3. Create new template (copy of the original)
            const copiedTemplateData = {
              name: newName,
              description: fullTemplate.description,
              notes: fullTemplate.notes,
              exercises: fullTemplate.exercises
            };

            const createdTemplate = await workoutAPI.createTemplate(copiedTemplateData, currentProgramId);
            newTemplateId = createdTemplate.id;
            newTemplateName = createdTemplate.name;

            // Add to inlineTemplates list for future references
            setInlineTemplates(prev => [...prev, {
              id: createdTemplate.id,
              name: createdTemplate.name,
              description: createdTemplate.description,
              is_inline: true
            }]);

          } catch (err) {
            console.error('Error copying template:', err);
            // If template copy fails, fall back to using the same template
            console.warn('Failed to copy template, reusing original template');
          }
        }

        // Create new workout with the template
        newWorkouts.push({
          id: Date.now() + Math.random() * 1000000, // Unique ID
          template_id: newTemplateId,
          template_name: newTemplateName,
          phase: w.phase,
          week: targetWeek,
          day: w.day,
          name: replaceWeekInName(w.name, sourceWeek, targetWeek),
          sort_order: maxSortOrder + index + 1,
          notes: w.notes,
          cardio_overrides: w.cardio_overrides
        });
      }

      // Update state with new workouts
      setFormData(prev => ({
        ...prev,
        workouts: [...prev.workouts, ...newWorkouts]
      }));

      // Auto-expand target week
      const targetNodeId = sourcePhase !== null
        ? getNodeId('week', targetWeek, getNodeId('phase', sourcePhase))
        : getNodeId('week', targetWeek);

      setExpandedNodes(prev => new Set([...prev, targetNodeId]));

      // Scroll to first new workout
      setTimeout(() => {
        const firstNewWorkout = newWorkouts[0];
        const element = document.getElementById(`workout-${firstNewWorkout.id}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);

      setCopyWeekSource(null);

    } catch (err) {
      console.error('Error copying week:', err);
      alert('Failed to copy week. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [copyWeekSource, formData.workouts, replaceWeekInName, inlineTemplates, availableTemplates, generateSmartTemplateName, program, autoSavedProgramId]);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Program name is required';
    }

    if (formData.workouts.length === 0) {
      newErrors.workouts = 'Add at least one workout to the program';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSaving(true);

    try {
      // Sort workouts by phase, week, and day before assigning sort_order
      // This ensures workouts execute in the correct order based on their day assignments
      const sortedWorkouts = [...formData.workouts].sort((a, b) => {
        // Sort by phase first (nulls last)
        if (a.phase === null && b.phase !== null) return 1;
        if (a.phase !== null && b.phase === null) return -1;
        if (a.phase !== b.phase) return (a.phase || 0) - (b.phase || 0);

        // Then by week (nulls last)
        if (a.week === null && b.week !== null) return 1;
        if (a.week !== null && b.week === null) return -1;
        if (a.week !== b.week) return (a.week || 0) - (b.week || 0);

        // Then by day (nulls last)
        if (a.day === null && b.day !== null) return 1;
        if (a.day !== null && b.day === null) return -1;
        return (a.day || 0) - (b.day || 0);
      });

      // Prepare data for submission with properly ordered sort_order
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        notes: formData.notes.trim(),
        workouts: sortedWorkouts.map((w, index) => ({
          template_id: w.template_id,
          phase: w.phase,
          week: w.week,
          day: w.day,
          name: w.name,
          sort_order: index,  // Now based on phase/week/day order, not creation order
          notes: w.notes,
          cardio_overrides: w.cardio_overrides || null
        }))
      };

      // Pass autoSavedProgramId to parent so it knows to update instead of create
      await onSave(submitData, autoSavedProgramId);
      onHide();
    } catch (err) {
      console.error('Error submitting program:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Prepare template options with grouping
  const templateOptions = [
    {
      label: 'Inline Templates (This Program)',
      options: inlineTemplates.map(t => ({
        value: t.id,
        label: t.name,
        isInline: true
      }))
    },
    {
      label: 'Global Templates',
      options: availableTemplates.map(t => ({
        value: t.id,
        label: t.name,
        isInline: false
      }))
    }
  ].filter(group => group.options.length > 0); // Only show groups that have templates

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        backdrop="static"
        keyboard={!isSaving}
        scrollable
      >
      <Modal.Header closeButton>
        <Modal.Title>
          {program || autoSavedProgramId ? 'Edit Program' : 'Create New Program'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '70vh' }}>
          {/* Auto-save notification */}
          {autoSavedProgramId && !program && (
            <Alert variant="info" className="mb-3">
              <i className="fas fa-check-circle me-2"></i>
              Program saved! You can now create inline templates. Don't forget to add workouts and click "Create Program" when done.
            </Alert>
          )}

          {/* Program Details */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Program Details
              </h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Program Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  isInvalid={!!errors.name}
                  placeholder="e.g., 12-Week Strength Program"
                  disabled={isSaving}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your program's goals and structure..."
                  disabled={isSaving}
                />
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any additional notes about this program..."
                  disabled={isSaving}
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Add/Edit Workout Section */}
          <Card className="mb-4">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className={`fas ${editingWorkoutId ? 'fa-edit' : 'fa-plus-circle'} me-2`}></i>
                  {editingWorkoutId ? 'Edit Workout' : 'Add Workout'}
                </h5>
                <div>
                  {editingWorkoutId && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      className="me-2"
                    >
                      <i className="fas fa-times me-1"></i>
                      Cancel Edit
                    </Button>
                  )}
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleCreateInlineTemplate}
                    disabled={isSaving}
                    title={!formData.name.trim()
                      ? "Enter a program name first to create inline templates"
                      : "Create a new template for this program only"}
                  >
                    <i className="fas fa-file-medical me-1"></i>
                    Create Inline Template
                  </Button>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="add-workout-toolbar">
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <Form.Label>Template *</Form.Label>
                    <div className="d-flex gap-2">
                      <div className="flex-grow-1">
                        <Select
                          value={selectedTemplate}
                          onChange={setSelectedTemplate}
                          options={templateOptions}
                          isLoading={isLoadingTemplates}
                          placeholder="Select a workout template..."
                          isDisabled={isSaving}
                          isClearable
                          formatGroupLabel={(group) => (
                            <div style={{ fontWeight: 'bold', color: '#495057' }}>
                              {group.label}
                            </div>
                          )}
                        />
                      </div>
                      {selectedTemplate && (
                        <Button
                          variant="outline-info"
                          onClick={handleCopyAndCustomize}
                          disabled={isSaving || copyingTemplate}
                          title="Copy this template and customize for this program"
                        >
                          {copyingTemplate ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              <i className="fas fa-copy"></i>
                            </>
                          ) : (
                            <i className="fas fa-copy"></i>
                          )}
                        </Button>
                      )}
                      {selectedTemplate && selectedTemplate.isInline && (
                        <Button
                          variant="outline-primary"
                          onClick={() => handleEditInlineTemplate(selectedTemplate)}
                          disabled={isSaving}
                          title="Edit this inline template"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                      )}
                    </div>
                    {errors.template && (
                      <div className="text-danger small mt-1">{errors.template}</div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <Form.Label>Workout Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={workoutName}
                      onChange={(e) => setWorkoutName(e.target.value)}
                      placeholder="e.g., Week 1 Day 1 - Push"
                      isInvalid={!!errors.workoutName}
                      disabled={isSaving}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.workoutName}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Leave blank to use template name: "{selectedTemplate?.label || 'Select a template'}"
                    </Form.Text>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-3">
                    <Form.Label className="small">Phase (optional)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={phase}
                      onChange={(e) => setPhase(e.target.value)}
                      disabled={isSaving}
                      isInvalid={!!errors.phase}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phase}
                    </Form.Control.Feedback>
                  </div>

                  <div className="col-md-3">
                    <Form.Label className="small">Week (optional)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={week}
                      onChange={(e) => setWeek(e.target.value)}
                      disabled={isSaving}
                      isInvalid={!!errors.week}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.week}
                    </Form.Control.Feedback>
                  </div>

                  <div className="col-md-3">
                    <Form.Label className="small">Day (optional)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={day}
                      onChange={(e) => setDay(e.target.value)}
                      disabled={isSaving}
                      isInvalid={!!errors.day}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.day}
                    </Form.Control.Feedback>
                  </div>

                  <div className="col-md-3 d-flex align-items-end">
                    <Button
                      variant="primary"
                      onClick={handleAddWorkout}
                      disabled={isSaving}
                      className="w-100"
                    >
                      <i className={`fas ${editingWorkoutId ? 'fa-check' : 'fa-plus'} me-2`}></i>
                      {editingWorkoutId ? 'Update Workout' : 'Add Workout'}
                    </Button>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Workouts List */}
          <Card>
            <Card.Header>
              <h5 className="mb-0">
                <i className="fas fa-list me-2"></i>
                Program Workouts ({formData.workouts.length})
              </h5>
            </Card.Header>
            <Card.Body>
              {errors.workouts && (
                <Alert variant="warning" className="mb-3">
                  {errors.workouts}
                </Alert>
              )}

              <WorkoutTreeView
                workouts={formData.workouts}
                expandedNodes={expandedNodes}
                onToggle={toggleNode}
                onExpandAll={expandAll}
                onCollapseAll={collapseAll}
                onWorkoutChange={handleWorkoutChange}
                onRemove={handleRemoveWorkout}
                onEdit={handleEditWorkout}
                onEditTemplate={handleEditTemplateFromWorkout}
                onEditOverrides={handleEditOverrides}
                onCopyWeek={handleCopyWeek}
                inlineTemplates={inlineTemplates}
                isSaving={isSaving}
                draggedWorkout={draggedWorkout}
                dragOverWorkout={dragOverWorkout}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving}>
            {isSaving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                {program || autoSavedProgramId ? 'Update Program' : 'Create Program'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
      </Modal>

      {/* Template Form Modal for creating/editing inline templates */}
      {showTemplateForm && (
        <TemplateForm
          show={showTemplateForm}
          onHide={() => {
            setShowTemplateForm(false);
            setEditingTemplate(null);
          }}
          template={editingTemplate}
          onSave={handleSaveTemplate}
          error={null}
        />
      )}

      {/* Cardio Overrides Editor Modal */}
      {showOverridesEditor && (
        <CardioOverridesEditor
          show={showOverridesEditor}
          onHide={() => {
            setShowOverridesEditor(false);
            setEditingOverridesWorkoutId(null);
            setCurrentOverridesTemplate(null);
          }}
          template={currentOverridesTemplate}
          existingOverrides={
            formData.workouts.find(w => w.id === editingOverridesWorkoutId)?.cardio_overrides
          }
          onSave={handleSaveOverrides}
        />
      )}

      {/* Copy Week Modal */}
      {showCopyWeekModal && copyWeekSource && (
        <CopyWeekModal
          show={showCopyWeekModal}
          onHide={() => {
            setShowCopyWeekModal(false);
            setCopyWeekSource(null);
          }}
          sourceWeek={copyWeekSource.week}
          sourcePhase={copyWeekSource.phase}
          allWorkouts={formData.workouts}
          onConfirm={handleCopyWeekConfirm}
        />
      )}
    </>
  );
}

export default ProgramForm;
